import React, { useMemo, useState, useEffect } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import { useFormikContext } from 'formik'
import { getUsersSubscribed } from '../../../../../services/teamExperienceService'
import { handleImageErrorProfile } from '../../../../../utils/UtilsFiles'
import { useNearBottom } from '../../../../../hooks/utils/useNearBottom'

const PAGE_SIZE = 10

type Props = {
    values: number[]
    onChange: (val: number[]) => void
    placeholder?: string
    minChars?: number
    initialSelected?: any[]
}

export function InviteePrivateAutocompleteField({
    values,
    onChange,
    placeholder = 'List of invitees',
    minChars = 0,
    initialSelected = [],
}: Props) {

    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const formik = useFormikContext()
    const [usersSubscribed, setUsersSubscribed] = useState<any[]>([])
    const [allFetchedUsers, setAllFetchedUsers] = useState<any[]>(initialSelected)
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

    useEffect(() => {
        if (initialSelected && initialSelected.length > 0) {
            setAllFetchedUsers((prev) => {
                const merged = [...prev]

                initialSelected.forEach((item) => {
                    if (!merged.some((existing) => existing.id === item.id)) {
                        merged.push(item)
                    }
                })

                return merged
            })
        }
    }, [initialSelected])

    const fetchData = async (search: string) => {
        const usersSubscribed_ = await getUsersSubscribed(search)
        const results = usersSubscribed_?.results || []

        setUsersSubscribed(results)

        setAllFetchedUsers((prev) => {
            const merged = [...prev]

            results.forEach((item) => {
                if (!merged.some((existing) => existing.id === item.id)) {
                    merged.push(item)
                }
            })

            return merged
        })
    }

    // debounce
    const [debounced, setDebounced] = useState('')

    useEffect(() => {
        const id = setTimeout(() => setDebounced(query.trim()), 200)
        return () => clearTimeout(id)
    }, [query])

    useEffect(() => {
        if (debounced.length < minChars) {
            if (debounced.length === 0) {
                // fetchData('')
            }
            return
        }

        fetchData(debounced.toLowerCase())
    }, [debounced, minChars])

    useEffect(() => {
        setVisibleCount(PAGE_SIZE)
    }, [usersSubscribed])

    const visible = useMemo(() => {
        return usersSubscribed.slice(0, visibleCount)
    }, [usersSubscribed, visibleCount])

    const hasMore = visibleCount < usersSubscribed.length

    const handleScroll = useNearBottom(() => {
        if (hasMore) {
            setVisibleCount((count) =>
                Math.min(count + PAGE_SIZE, usersSubscribed.length)
            )
        }
    })

    const highlightMatch = (text: string, query: string) => {
        if (!query) return text

        const regex = new RegExp(
            `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
            'gi'
        )

        const parts = text.split(regex)

        return (
            <>
                {parts.map((part, index) =>
                    regex.test(part) ? (
                        <span key={index} style={{ fontWeight: 700 }}>
                            {part}
                        </span>
                    ) : (
                        <span key={index} style={{ fontWeight: 300 }}>
                            {part}
                        </span>
                    )
                )}
            </>
        )
    }

    const handleAdd = (itemId: number) => {
        if (!values.includes(itemId)) {
            onChange([...values, itemId])
        }

        setQuery('')
        setOpen(false)
    }

    const handleRemove = (itemId: number) => {
        onChange(values.filter((v) => v !== itemId))
    }

    const validateForm = () => {
        formik.setFieldTouched('allowed_guest_ids', true)
        formik.validateForm()
    }

    const selectedObjects = values
        .map((id) => allFetchedUsers.find((i) => i.id === id))
        .filter(Boolean)

    return (
        <div className="nb-language-autocomplete">
            <div className="nb-language-wrapper">
                {selectedObjects.map((item: any) => (
                    <div
                        key={item.id}
                        className="tap-add-experience-2-category-pill tap-add-experience-2-category-pill--selected"
                    >
                        <div className="tap-add-experience-2-image">
                            <img
                                src={item.image}
                                width={24}
                                height={24}
                                onError={handleImageErrorProfile}
                            />
                        </div>

                        <span>{item.name}</span>

                        <div onClick={() => handleRemove(item.id)}>
                            <KTSVG
                                path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')}
                            />
                        </div>
                    </div>
                ))}

                <input
                    className="tap-add-experience-input-category nb-language-input"
                    placeholder={placeholder}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value)
                        setOpen(true)
                        validateForm()
                    }}
                    onFocus={() => {
                        setOpen(true)
                        validateForm()
                    }}
                    onBlur={() => setTimeout(() => setOpen(false), 120)}
                />
            </div>

            {open && visible.length > 0 && (
                <div className="nb-language-menu" onScroll={handleScroll}>
                    {visible.map((item) => (
                        <div
                            key={item.id}
                            className="nb-language-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleAdd(item.id)}
                        >
                            {highlightMatch(item.name, debounced)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
